import {
  Link as LinkIcon,
  MoreVert as MoreVertIcon,
  NoteAdd as NoteAddIcon,
} from "@mui/icons-material";
import { SwipeableDrawer } from "@mui/material";
import { styled } from "@mui/material/styles";
import { unwrapResult } from "@reduxjs/toolkit";
import { AddDocumentWebLinkDialog } from "components/job/files/AddDocumentWebLinkDialog";
import PrimaryButton from "components/PrimaryButton";
import StyledFab from "components/StyledFab";
import { isAbortError } from "helpers";
import { TabProps } from "models/TabProps";
import { nanoid } from "nanoid";
import { DocumentWebLinkType, FileType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import Resizer from "react-image-file-resizer";
import { FormattedMessage } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import {
  addFile,
  addLink,
  addWebLink,
  getFiles,
  getLinks,
  setEditLinkOpen,
  updateLink,
  uploadFile,
} from "store/slices/partner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

const StyledContainer = styled("div")(() => ({
  zIndex: "9001",
}));

type FileTabProps = TabProps & {
  isFullSize: boolean;
};

export const FileTabActions: FC<FileTabProps> = (props) => {
  const { setFab, isFullSize } = props;
  const dispatch = useAppDispatch();
  const { autoSyncDocuments } = useAppSelector((s) => s.user);
  const job = useAppSelector(selectPageSelectedJob);
  const { openEditLink, linkToEdit } = useAppSelector((s) => s.jobs.fileTab);
  const documentWebLinksEnabled = useAppSelector((s) => isFlagEnabled(s, "DocumentWebLinks"));
  const [openDrawer, setOpenDrawer] = useState(false);

  const resizeFile = useCallback(
    (file: Blob) =>
      new Promise((resolve) => {
        if (file.type.includes("image/") && !isFullSize) {
          Resizer.imageFileResizer(
            file,
            600,
            600,
            "JPG",
            80,
            0,
            (uri) => {
              resolve(uri);
            },
            "base64",
          );
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(file);
        }
      }),
    [isFullSize],
  );

  const handleFileInput = useCallback(
    (e: any) => {
      const files = e.target.files;
      for (const file of files) {
        if (file) {
          resizeFile(file).then((data) => {
            const output = typeof data === "string" ? data : "";
            const f: FileType = {
              id: nanoid(),
              name: file.name,
              data: output,
              description: "",
              size: output.length,
              isFullSize: isFullSize,
              fileType: file.type,
              useInServiceReport: false,
            };
            if (autoSyncDocuments) {
              dispatch(uploadFile({ jobId: job.id, file: f })).catch((error: any) => {
                if (isAbortError(error)) return;
                dispatch(addSnackbarMessage({ key: "UploadFile-fail" }));
              });
            } else {
              dispatch(addFile({ file: f }));
            }
          });
        }
      }
      if (autoSyncDocuments) dispatch(getFiles());
      e.target.value = null;
    },
    [autoSyncDocuments, dispatch, isFullSize, job.id, resizeFile],
  );

  const handleUpdateLink = useCallback(
    (url: string, name: string, original?: DocumentWebLinkType | null) => {
      if (original) {
        dispatch(
          updateLink({
            link: {
              ...original,
              link: url,
              name: name,
            },
          }),
        );
      } else {
        const link: DocumentWebLinkType = {
          id: nanoid(),
          link: url,
          name,
        };
        if (autoSyncDocuments) {
          dispatch(addWebLink({ jobId: job.id, links: [link] }))
            .then(unwrapResult)
            .then(() => {
              dispatch(getLinks());
            })
            .catch((error: any) => {
              if (isAbortError(error)) return;
              dispatch(addSnackbarMessage({ key: "UploadFile-fail" }));
            });
        } else {
          dispatch(addLink({ link }));
        }
      }
      dispatch(setEditLinkOpen({ open: false, link: null }));
      setOpenDrawer(false);
    },
    [autoSyncDocuments, dispatch, job.id],
  );

  useEffect(() => {
    const fab = (
      <StyledFab onClick={() => setOpenDrawer(true)} data-testid="fileTabActionsOpenButton">
        <MoreVertIcon />
      </StyledFab>
    );
    if (setFab) {
      setFab(fab);
    }
  }, [setFab]);

  return (
    <StyledContainer data-testid="jobDetailsActionsContainer">
      <SwipeableDrawer
        anchor="bottom"
        open={openDrawer}
        onOpen={() => setOpenDrawer(true)}
        onClose={() => setOpenDrawer(false)}
      >
        <div style={{ margin: "0px 16px" }} data-testid="jobDetailsActionsDrawer">
          <label htmlFor="upload-button-file">
            <PrimaryButton
              data-testid="syncOrderLines"
              startIcon={<NoteAddIcon />}
              style={{ textTransform: "none" }}
            >
              <FormattedMessage id="file.addFile" />
            </PrimaryButton>
          </label>

          {documentWebLinksEnabled && (
            <PrimaryButton
              data-testid="changeJobMachineButton"
              onClick={() => dispatch(setEditLinkOpen({ open: true, link: null }))}
              startIcon={<LinkIcon />}
              style={{ textTransform: "none" }}
            >
              <FormattedMessage id="file.addLink" />
            </PrimaryButton>
          )}
        </div>
      </SwipeableDrawer>
      <input
        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
        multiple
        style={{ display: "none" }}
        id="upload-button-file"
        type="file"
        data-testid="FileTab-FileInput"
        onChange={handleFileInput}
      />
      {documentWebLinksEnabled && (
        <AddDocumentWebLinkDialog
          open={openEditLink}
          link={linkToEdit}
          externalId={job.externalId}
          updateLink={handleUpdateLink}
          onClose={() => dispatch(setEditLinkOpen({ open: false, link: null }))}
        />
      )}
    </StyledContainer>
  );
};
